// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { holdsInitialisedUtilisationTE } from '../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { isLeafNode } from '../../../../functions/client-model/client-node/is-leaf-node';
import { transformUnit } from '../../../../functions/transform-unit';
import { Dict } from '../../../../model/dict';
import { Language } from '../../../../model/enums/language';
import { MeasurementSystem } from '../../../../model/enums/measurement-system';
import { TransitionElement } from '../../../../model/enums/transition-element';
import { PersistedTransitionProperties } from '../../../../model/transition-targets/persisted/persisted-transition-properties';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { CCCModelUtility } from '../../../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import {
  ModelTranslationOutputType,
  generateModelTranslationKey,
} from '../../../../services/climate-engine/ccc-model-utility/generate-model-translation-key';
import { TranslationService } from '../../../../services/translation-service';
import { isMetricEnergyUnit } from '../../../../services/unit-conversion/is-metric-energy-unit';
import { UnitConversionService } from '../../../../services/unit-conversion/unit-conversion.service';
import { LabelsV4 } from '../../model/dashboard-additional-data';

@Injectable({
  providedIn: 'root',
})
export class LabelCreatorV4Service {
  private _language: Language;
  private _measurementSystem: MeasurementSystem;

  constructor(
    private _cccModelUtility: CCCModelUtility,
    private _unitConversionService: UnitConversionService,
    private _translationService: TranslationService
  ) {}

  createLabels(board: ClientBoardContainer): Dict<LabelsV4> {
    this._language = board.boardSettings.internationalisation.defaultLanguage;
    this._measurementSystem = board.boardSettings.measurementSystem ?? MeasurementSystem.Metric;
    const transitionElements = Object.values(board.nodes).filter(
      (n) => isLeafNode(n) && holdsInitialisedUtilisationTE(n)
    );

    const labelsDict = transitionElements.reduce<Dict<LabelsV4>>((dict, te) => {
      dict[te.id] = {
        transitionUnit: this._translateTEUnit(
          te.nodeProperties?.transitionTargetData?.transitionTargetProperties?.transitionElement,
          te.nodeProperties.transitionTargetData?.transitionProperties
        ),
        transitionKind: this._translateTEKind(
          te.nodeProperties?.transitionTargetData?.transitionTargetProperties?.transitionElement,
          te.nodeProperties.transitionTargetData?.transitionProperties
        ),
      };
      return dict;
    }, {});
    return labelsDict;
  }

  private _translateTEUnit(te: TransitionElement, properties: PersistedTransitionProperties): string {
    /*
    * Saving this for when series display other values
    const isParameterUpdate = properties.weightType === ModelsTargetType.ParameterUpdate;
    const unit = isParameterUpdate
      ? transformUnit(
          this._cccModelUtility.getPrimaryUpdatedParameterUnit(te),
          this._measurementSystem,
          this._unitConversionService
        )
      : transformUnit(
          this._cccModelUtility.getDefaultActivityUnit(te),
          this._measurementSystem,
          this._unitConversionService
        );
    */

    const findLabelUnit = (): string => {
      const metricUnit = this._cccModelUtility.getDefaultActivityUnit(te);
      const isEnergyUnit = isMetricEnergyUnit(metricUnit);
      if (isEnergyUnit) {
        const usEnergyUnit = this._cccModelUtility.getUSEnergyUnitOfTransitionElement(te);
        const unit = this._unitConversionService.transformEnergyUnit(
          MeasurementSystem.Metric,
          this._measurementSystem,
          metricUnit,
          usEnergyUnit
        );
        return unit;
      } else {
        return transformUnit(metricUnit, this._measurementSystem, this._unitConversionService);
      }
    };

    const key = generateModelTranslationKey(ModelTranslationOutputType.Unit, findLabelUnit());
    return this._translationService.translate(this._language, key);
  }

  private _translateTEKind(te: TransitionElement, properties: PersistedTransitionProperties): string {
    /*
    * Saving this for when series display other values
    const isParameterUpdate = properties.weightType === ModelsTargetType.ParameterUpdate;
    let kind = '';
    if (isParameterUpdate) {
      const key = generateModelTranslationKey(
        ModelTranslationOutputType.VariableTitle,
        this._cccModelUtility.getPrimaryUpdatedVariable(te)
      );
      kind = this._translationService.translate(this._language, key);
    } else {
      const key = generateModelTranslationKey(ModelTranslationOutputType.TransitionElementMethodTo, te);
      kind = this._translationService.translate(this._language, key);
    }
    */
    const key = generateModelTranslationKey(ModelTranslationOutputType.TransitionElementMethodTo, te);
    const kind = this._translationService.translate(this._language, key);
    return `${this._translateTEUnit(te, properties)} ${kind}`;
  }
}
